import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { Search, Moon, Sun, Bell, SlidersHorizontal, ClipboardCheck, UserPlus } from 'lucide-react';
import { IconBtn, Pop } from './primitives';
import { useThemeMode } from './themeMode';
import TweaksPanel from './TweaksPanel';
import CourseFilterDropdown from './CourseFilterDropdown';
import { useCourseFilter } from '../context/CourseFilterContext';
import { hidesPageTitle, resolvePage, showsCourseFilter } from './topBarRoutes';

/* ============================================================
   Shared portal top app bar — mirrors the "topbar" block from the
   StudYaK Instructor Panel v2 design (crumb + page title on the
   left, search in the middle, icon actions on the right).
   Presentation only: search / help are visual placeholders; the
   dark-mode button reuses the existing toggle; the notifications
   bell opens a real Pop dropdown (local placeholder data).
   ============================================================ */

const Bar = styled.header`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 28px;
  height: 68px;
  flex: none;
  background: ${({ theme }) => `color-mix(in oklch, ${theme.surface} 80%, transparent)`};
  backdrop-filter: blur(10px);
  border-bottom: 1px solid ${({ theme }) => theme.border};
  position: sticky;
  top: 0;
  z-index: 5;

  @media (max-width: 600px) {
    padding: 0 16px;
    gap: 10px;
  }
`;

const Title = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  .crumb {
    font-size: ${({ theme }) => theme.fsMicro};
    color: ${({ theme }) => theme.textSubtle};
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
    white-space: nowrap;
  }
  .head {
    font-family: ${({ theme }) => theme.fontHead};
    font-size: 21px;
    font-weight: 750;
    letter-spacing: -.02em;
    margin: 1px 0 0;
    white-space: nowrap;
    color: ${({ theme }) => theme.text};
  }

  @media (max-width: 600px) {
    .head { font-size: 18px; }
    .crumb { display: none; }
  }
`;

const Spacer = styled.div`
  flex: 1;
`;

const SearchBox = styled.label`
  display: flex;
  align-items: center;
  gap: 9px;
  height: ${({ theme }) => theme.controlH};
  padding: 0 14px;
  width: 280px;
  max-width: 30vw;
  background: ${({ theme }) => theme.surface3};
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.rMd};
  color: ${({ theme }) => theme.textMuted};

  &:focus-within {
    border-color: ${({ theme }) => theme.accentLine};
    background: ${({ theme }) => theme.surface};
  }

  input {
    border: none;
    background: none;
    outline: none;
    width: 100%;
    font-size: 13px;
    color: ${({ theme }) => theme.text};
  }

  @media (max-width: 600px) { display: none; }
`;

const PopAnchor = styled.div`
  position: relative;
  flex: none;

  .notif-title { font-size: 13px; font-weight: 600; color: ${({ theme }) => theme.text}; line-height: 1.35; }
  .notif-time { font-size: ${({ theme }) => theme.fsMicro}; color: ${({ theme }) => theme.textSubtle}; margin-top: 2px; }
  .notif-ico {
    flex: none; width: 30px; height: 30px; border-radius: ${({ theme }) => theme.rSm};
    display: grid; place-items: center;
    background: ${({ theme }) => `color-mix(in oklch, ${theme.accent} 12%, ${theme.surface})`};
    color: ${({ theme }) => theme.accent};
  }
`;

/* Placeholder notifications (no backend yet) — local state only. */
const SAMPLE_NOTIFS = [
  { id: 1, icon: ClipboardCheck, title: 'New submissions awaiting your review', time: '5 min ago', unread: true },
  { id: 2, icon: UserPlus, title: 'New learners enrolled in your course', time: '1 hour ago', unread: true },
];

function TopBar({ tweaks, setTweak }) {
  const location = useLocation();
  const { mode, toggleModeFromEvent } = useThemeMode();
  const { crumb, title } = resolvePage(location.pathname);
  const isDark = mode === 'dark';
  const showCourseFilter = showsCourseFilter(location.pathname);
  const hideTitle = hidesPageTitle(location.pathname);
  const { selectedCourseId, setSelectedCourse } = useCourseFilter();

  const [notifs, setNotifs] = useState(SAMPLE_NOTIFS);
  const [notifOpen, setNotifOpen] = useState(false);
  const [tweaksOpen, setTweaksOpen] = useState(false);
  const notifRef = useRef(null);
  const unreadCount = notifs.filter((n) => n.unread).length;

  const markAllRead = useCallback((e) => {
    e.stopPropagation();
    setNotifs((list) => list.map((n) => ({ ...n, unread: false })));
  }, []);

  // Close the notifications popover on outside click / Escape.
  useEffect(() => {
    if (!notifOpen) return undefined;
    const onDown = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setNotifOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [notifOpen]);

  return (
    <Bar>
      {!hideTitle && (
        <Title>
          <span className="crumb">{crumb}</span>
          <span className="head">{title}</span>
        </Title>
      )}

      <Spacer />

      {showCourseFilter && (
        <CourseFilterDropdown
          value={selectedCourseId}
          onChange={setSelectedCourse}
        />
      )}

      <SearchBox>
        <Search size={16} />
        <input type="text" placeholder="Search courses, people…" aria-label="Search" />
      </SearchBox>

      <IconBtn
        type="button"
        onClick={toggleModeFromEvent}
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        aria-label="Toggle color mode"
      >
        {isDark ? <Sun size={18} /> : <Moon size={18} />}
      </IconBtn>

      <PopAnchor ref={notifRef}>
        <IconBtn
          type="button"
          className={notifOpen ? 'is-open' : ''}
          onClick={() => setNotifOpen((v) => !v)}
          title="Notifications"
          aria-label="Notifications"
          aria-expanded={notifOpen}
        >
          <Bell size={18} />
          {unreadCount > 0 && <span className="dot" />}
        </IconBtn>

        {notifOpen && (
          <Pop style={{ left: 'auto', right: 0, width: 340, maxWidth: '86vw' }} role="menu">
            <div className="pop__head">
              <span>Notifications</span>
              {unreadCount > 0 && (
                <button type="button" className="pop__link" onClick={markAllRead}>Mark all read</button>
              )}
            </div>

            {notifs.length === 0 ? (
              <div className="pop__empty">You&apos;re all caught up.</div>
            ) : (
              notifs.map((n) => {
                const Icon = n.icon;
                return (
                  <div key={n.id} className={`pop__row${n.unread ? ' is-unread' : ''}`} role="menuitem">
                    <span className="notif-ico"><Icon size={16} /></span>
                    <div style={{ minWidth: 0 }}>
                      <div className="notif-title">{n.title}</div>
                      <div className="notif-time">{n.time}</div>
                    </div>
                  </div>
                );
              })
            )}

            <div className="pop__foot" onClick={() => setNotifOpen(false)}>View all notifications</div>
          </Pop>
        )}
      </PopAnchor>

      <IconBtn type="button" onClick={() => setTweaksOpen((v) => !v)} title="Customise appearance" aria-label="Customise appearance">
        <SlidersHorizontal size={18} />
      </IconBtn>
      {tweaksOpen && <TweaksPanel tweaks={tweaks} setTweak={setTweak} onClose={() => setTweaksOpen(false)} />}
    </Bar>
  );
}

export default TopBar;
